import { useMemo } from 'react';
import { useJpa, useRequest } from '@stores';
import { connect } from 'redux-bundler-react';

import GeneralProjectInformation from '@forms/input-forms/common/GeneralProjectInformation';
import NprFormInstructions from '@forms/static-forms/Npr/NprFormInstructions';
import Contacts from '@forms/input-forms/common/Contacts';
import NprFormCertifySignSubmit from '@forms/input-forms/NprForm/NprCertifySignSubmit';
import NprInformation from '@forms/input-forms/NprForm/NprInformation';
import SupportingInformation from '@forms/input-forms/common/SupportingInformation';
import JpaInfo from './JpaInfo';
import JpaForm, { generateJpaFormName } from './JpaForm';
import { NprResultTypes, RequestType } from '@src/utils/enums';
import SelfVerifyOutcomePreview from '../components/Form/SelfVerifyOutcomePreview';
import { nprOutcomes } from '@pages/Forms/input-forms/NprForm/nprHelpers';

//List of steps for vertical stepper, these can also be used for form header. index = step ID
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getNprFormMetadata = ({ variant: formVariant, version }) => {
  const isAbNpr = formVariant !== 'npr';

  const baseFormArray = [
    NprFormInstructions.metadata,
    Contacts.metadata,
    GeneralProjectInformation.metadata,
    NprInformation.metadata,
    SupportingInformation.metadata,
    NprFormCertifySignSubmit.metadata,
  ];
  const jpaBasicInfo = useJpa.getState().jpaBasicInfo;
  const isJpa = jpaBasicInfo?.length > 0;

  // insert JPA sidebar steps
  if (isJpa) {
    const jpaInfoSteps = [{ sectionName: 'Location Specific Information', isParentStep: true }];
    const jpaFormSteps = [{ sectionName: 'Location Specific Questions', isParentStep: true }];

    jpaBasicInfo.forEach((jpa) => {
      jpaInfoSteps.push({ sectionName: generateJpaFormName(jpa), isSubStep: true, parentId: 1 });
      jpaFormSteps.push({
        sectionName: generateJpaFormName(jpa),
        isSubStep: true,
        parentId: jpaBasicInfo?.length + baseFormArray.length - 1,
      });
    });

    baseFormArray.splice(1, 0, ...jpaInfoSteps);
    baseFormArray.splice(baseFormArray.length - 2, 0, ...jpaFormSteps);
  }

  if (!isAbNpr) {
    const currentPermitTitle = baseFormArray[3].sectionName;
    baseFormArray[3] = {
      ...baseFormArray[3],
      sectionName: currentPermitTitle.replace(' (Activity Based)', ''),
    };
  }

  return baseFormArray;
};

const generateSections = ({
  doUpdateNprRequest,
  isAbNpr,
  isJpa,
  jpaBasicInfo,
  nprData,
  nprID,
  formKey,
  formVersion,
  formVariant,
}) => {
  const sections = [
    {
      id: 'NprInstructions',
      name: 'Instructions',
      content: NprFormInstructions,
      props: { formKey, formVersion, formVariant },
    },
    {
      id: 'NprContacts',
      name: 'Contacts',
      content: Contacts,
      subtext: 'requesting confirmation that the proposed activity does not require authorization from the USACE.',
      componentID: nprID,
    },
    {
      id: 'GeneralProjectInformation',
      name: 'General Project Information',
      content: GeneralProjectInformation,
    },
    {
      id: 'NoPermitRequiredInformation',
      name: `${isAbNpr ? RequestType.ActivityBasedNoPermitRequired : RequestType.NoPermitRequired} Information`,
      content: NprInformation,
      componentID: nprID,
      props: { formKey, formVersion, formVariant },
    },
    {
      id: 'SupportingInformation',
      name: 'Supporting Information',
      content: SupportingInformation,
      componentID: nprID,
      subtext: (
        <p>
          Please upload any supporting documentation including information related to the request. Multiple documents of
          the same file type can be combined and uploaded as one document.
        </p>
      ),
      updateRequestData: doUpdateNprRequest,
    },
    {
      id: 'NprCertifySignSubmit',
      name: 'Certify Sign Submit',
      content: NprFormCertifySignSubmit,
      props: { formKey, formVersion, formVariant },
    },
  ];
  // insert JPA content
  if (isJpa) {
    const jpaInfoSections = [
      {
        id: 'jpa-info',
        name: 'Location Specific Information',
        content: () => <></>,
      },
    ];
    const jpaFormSections = [
      {
        id: 'jpa-questions',
        name: 'Location Specific Information',
        content: () => <></>,
      },
    ];

    const infoStartIndex = 1;
    const formStartIndex = sections.length + jpaBasicInfo.length - 1;
    const infoGroupIndexes = [];
    const formGroupIndexes = [];

    jpaBasicInfo.forEach((_, jpaIndex) => {
      infoGroupIndexes.push(jpaIndex + infoStartIndex + 1);
      formGroupIndexes.push(jpaIndex + formStartIndex + 1);
    });

    jpaBasicInfo.forEach((jpa) => {
      jpaInfoSections.push({
        id: `jpa-info-${jpa.formID}`,
        name: generateJpaFormName(jpa),
        content: JpaInfo,
        componentId: nprID,
        formId: jpa.formID,
        group: {
          indexes: infoGroupIndexes,
          parentIndex: infoStartIndex,
          parentName: jpaInfoSections[0].name,
        },
        requestId: nprData.requestID,
        version: nprData.version,
      });
      jpaFormSections.push({
        id: `jpa-questions-${jpa.formID}`,
        name: generateJpaFormName(jpa),
        content: JpaForm,
        componentId: nprID,
        formId: jpa.formID,
        group: {
          indexes: formGroupIndexes,
          parentIndex: formStartIndex,
          parentName: jpaFormSections[0].name,
        },
        requestId: nprData.requestID,
        version: nprData.version,
      });
    });

    sections.splice(1, 0, ...jpaInfoSections);
    sections.splice(sections.length - 2, 0, ...jpaFormSections);
  }

  return sections;
};

const NprForm = connect(
  'doUpdateNprRequest',
  'selectNprID',
  'selectNprData',
  'selectRequestType',
  ({ doUpdateNprRequest, nprID, nprData, formKey, formVersion, formVariant }) => {
    const { getActiveSection } = useRequest();
    const { jpaBasicInfo } = useJpa();
    const isJpa = jpaBasicInfo?.length > 0;
    const isAbNpr = formVariant === 'abNpr';
    const activeStep = getActiveSection();

    const sections = useMemo(
      () =>
        generateSections({
          doUpdateNprRequest,
          isAbNpr,
          isJpa,
          jpaBasicInfo,
          nprData,
          nprID,
          formKey,
          formVersion,
          formVariant,
        }),
      [doUpdateNprRequest, formKey, formVariant, formVersion, isAbNpr, isJpa, jpaBasicInfo, nprData, nprID]
    );

    return (
      <>
        <SelfVerifyOutcomePreview
          outcomes={nprOutcomes}
          defaultResult={NprResultTypes.NprExemption}
          label={`Preview ${isAbNpr ? 'Activity-Based NPR' : 'NPR'} outcome`}
        />
        {sections.map((section, index) => {
          const { content: Content, ...props } = section;
          return (
            <section
              id={section.id}
              key={section.id}
              name={section.name}
              style={{ display: activeStep?.id === index ? 'inline' : 'none' }}
            >
              <Content stepNo={index} {...props} {...section.props} />
            </section>
          );
        })}
      </>
    );
  }
);

export default NprForm;
